import { parseConceptCues, parseConceptPresentation, parseTraceReference, parseTraceScenePlan } from '@/_pure/episode';
import { normalizeAuthoredBeat, parseNarrationPauses, parseNarrationTextMap } from '@/_pure/episodeAuthoring';
import { parseEpisodeComments } from '@/_pure/episodeComments';
import { parseEpisodeExcerpt } from '@/_pure/episodeExcerpt';
import { parseEpisodeVisualSources, resolveEpisodeVisualSource } from '@/_pure/episodeVisualSource';
import { parseWorkflowSceneDirection } from '@/workflow-runner/_pure/sceneDirection';
import { parsePresentationEffect } from '@/_pure/planPresentation';
import type { OperationPresentationMode } from '@/workflow/_contract/config';
import type { WorkflowConceptArrangement, WorkflowConceptBeat, WorkflowConceptTarget, WorkflowEpisode, WorkflowScene, WorkflowTraceTarget } from '@/workflow-runner/_contract/episode';
import type { RuntimeState } from '@/workflow-runner/_contract/runtime';
import type { WorkflowVisualSource } from '@/workflow-runner/_contract/visualSource';

function record(value: unknown): Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid Workflow episode object.');
    return value as Record<string, unknown>;
}
function text(value: unknown): string {
    if (typeof value !== 'string' || !value.trim()) throw new Error('Workflow episode requires nonempty text.');
    return value;
}
function fields(value: Record<string, unknown>, allowed: readonly string[]) {
    for (const key of Object.keys(value)) {
        if (!allowed.includes(key)) throw new Error(`Unknown Workflow episode field '${key}'.`);
    }
}
function parseConceptTarget(value: unknown): WorkflowConceptTarget {
    const target = record(value);
    if (target.targetKind === 'flag' || target.targetKind === 'connector') {
        fields(target, ['targetKind', 'itemKey']);
        return { targetKind: target.targetKind, itemKey: text(target.itemKey) };
    }
    if (target.targetKind === 'item') {
        fields(target, ['targetKind', 'itemKey']);
        return { targetKind: 'item', itemKey: text(target.itemKey) };
    }
    if (target.targetKind === 'constellation') {
        fields(target, ['targetKind', 'constellationKey']);
        return { targetKind: 'constellation', constellationKey: text(target.constellationKey) };
    }
    fields(target, ['targetKind']);
    if (target.targetKind === 'resource-type-ring' || target.targetKind === 'natural-resource-tour'
        || target.targetKind === 'selected-tool' || target.targetKind === 'selected-roles'
        || target.targetKind === 'invocation-inputs' || target.targetKind === 'invocation-outputs' || target.targetKind === 'invocation-stage') {
        return { targetKind: target.targetKind };
    }
    throw new Error('Unsupported Workflow concept emphasis target.');
}
function parseTraceTarget(value: unknown): WorkflowTraceTarget {
    const target = record(value);
    if (target.targetKind === 'branch-case') {
        fields(target, ['targetKind', 'caseIndex', 'slot']);
        if (typeof target.caseIndex !== 'number' || !Number.isInteger(target.caseIndex) || target.caseIndex < 0
            || (target.slot !== undefined && target.slot !== 'when' && target.slot !== 'what')) {
            throw new Error('Invalid Workflow BranchStep case target.');
        }
        return { targetKind: 'branch-case', caseIndex: target.caseIndex, ...(target.slot ? { slot: target.slot } : {}) };
    }
    if (target.targetKind === 'workflow') {
        fields(target, ['targetKind']);
        return { targetKind: 'workflow' };
    }
    if (target.targetKind === 'tool-step' || target.targetKind === 'entry' || target.targetKind === 'dependency') {
        fields(target, ['targetKind', 'execution', ...(target.targetKind === 'entry' ? ['direction', 'roleName']
            : target.targetKind === 'dependency' ? ['roleName'] : [])]);
        const execution = target.execution;
        if (typeof execution !== 'number' || !Number.isInteger(execution) || execution < 1) throw new Error('Invalid Workflow execution target.');
        if (target.targetKind === 'tool-step') return { targetKind: 'tool-step', execution };
        const roleName = text(target.roleName);
        if (target.targetKind === 'dependency') return { targetKind: 'dependency', execution, roleName };
        if (target.direction !== 'input' && target.direction !== 'output') throw new Error('Invalid Workflow entry direction.');
        return { targetKind: 'entry', execution, direction: target.direction, roleName };
    }
    const parsed = parseConceptTarget(value);
    if (parsed.targetKind === 'item' || parsed.targetKind === 'constellation' || parsed.targetKind === 'flag' || parsed.targetKind === 'connector') return parsed;
    throw new Error('Unsupported Workflow trace target.');
}
function validateTeachingMarks(marks: readonly string[] | undefined, targetKind: string) {
    if (marks && !(marks.length === 1 && marks[0] === 'contour')
        && (marks.length !== 2 || !marks.includes('contour') || !marks.includes('pointer'))) {
        throw new Error('Workflow teaching emphasis uses contour-only or contour and pointer marks.');
    }
    if (['flag', 'connector', 'dependency'].includes(targetKind) && marks?.includes('pointer')) {
        throw new Error('Workflow feature emphasis uses contours without pointers.');
    }
}
function parseConceptArrangement(value: unknown): WorkflowConceptArrangement {
    const arrangement = record(value);
    fields(arrangement, ['arrangementKind', 'focus', 'toolHandle', 'content', 'invocationStage']);
    if (arrangement.arrangementKind !== 'workflow-space'
        || (arrangement.focus !== 'overview' && arrangement.focus !== 'tools' && arrangement.focus !== 'resource-types'
            && arrangement.focus !== 'input-roles' && arrangement.focus !== 'output-roles')) {
        throw new Error('Invalid WorkflowSpace concept arrangement.');
    }
    if (arrangement.content === 'tool-invocation') {
        if (arrangement.focus !== 'overview' || arrangement.toolHandle !== undefined
            || (arrangement.invocationStage !== 'selected' && arrangement.invocationStage !== 'inputs'
                && arrangement.invocationStage !== 'invoked' && arrangement.invocationStage !== 'outputs'
                && arrangement.invocationStage !== 'tour')) {
            throw new Error('Concept Tool invocation requires overview focus and a supported invocationStage.');
        }
        return { arrangementKind: 'workflow-space', focus: 'overview', content: 'tool-invocation',
            invocationStage: arrangement.invocationStage };
    }
    if (arrangement.invocationStage !== undefined) throw new Error('Only Tool invocation arrangements have an invocationStage.');
    if (arrangement.content !== undefined
        && ((arrangement.content !== 'resource-types-only' && arrangement.content !== 'resource-types-and-resources'
            && arrangement.content !== 'mock-resource-types-and-resources' && arrangement.content !== 'product-resources'
            && arrangement.content !== 'sum-resources' && arrangement.content !== 'resource-type-universe'
            && arrangement.content !== 'tool-types')
            || (arrangement.focus !== 'overview' && arrangement.focus !== 'resource-types'))) {
        throw new Error('Workflow resource-type content requires supported content and overview or resource-types focus.');
    }
    if (arrangement.focus === 'input-roles' || arrangement.focus === 'output-roles') {
        if (arrangement.toolHandle !== 'TOOL-NaturalAdd' && arrangement.toolHandle !== 'TOOL-NaturalMultiply'
            && arrangement.toolHandle !== 'TOOL-NaturalLessThan') {
            throw new Error('Workflow role arrangement requires a supported tool definition.');
        }
        return { arrangementKind: 'workflow-space', focus: arrangement.focus, toolHandle: arrangement.toolHandle };
    }
    if (arrangement.toolHandle !== undefined) throw new Error('Only Workflow role arrangements select a tool definition.');
    if ((arrangement.content === 'resource-types-only' || arrangement.content === 'resource-types-and-resources'
        || arrangement.content === 'mock-resource-types-and-resources' || arrangement.content === 'product-resources'
        || arrangement.content === 'sum-resources' || arrangement.content === 'resource-type-universe'
        || arrangement.content === 'tool-types')
        && (arrangement.focus === 'overview' || arrangement.focus === 'resource-types')) {
        return { arrangementKind: 'workflow-space', focus: arrangement.focus, content: arrangement.content };
    }
    return arrangement.focus === 'tools'
        ? { arrangementKind: 'workflow-space', focus: 'tools' }
        : { arrangementKind: 'workflow-space', focus: arrangement.focus };
}
function parseWorkflowTrace(value: unknown) {
    const trace = parseTraceReference(value);
    if (trace.adapter !== 'workflow-space' || trace.traceKind !== 'WorkflowTrace'
        || trace.schemaVersion !== 1) throw new Error('Workflow scene requires a supported WorkflowTrace.');
    return trace;
}

function parseOperationPresentationMode(value: unknown): OperationPresentationMode {
    if (value !== 'panels' && value !== 'wheels') throw new Error('Workflow scene requires an OperationPresentationMode.');
    return value;
}

export function parseWorkflowVisualSources(value: unknown): ReadonlyMap<string, WorkflowVisualSource> {
    return parseEpisodeVisualSources(value, {
        parseTrace(source) {
            fields(source, ['trace', 'operationPresentationMode']);
            return { trace: parseWorkflowTrace(source.trace),
                operationPresentationMode: parseOperationPresentationMode(source.operationPresentationMode) };
        },
        parseArrangement: parseConceptArrangement,
    });
}

export function parseWorkflowEpisode(value: unknown, sources?: ReadonlyMap<string, WorkflowVisualSource>): WorkflowEpisode {
    const episode = record(value);
    fields(episode, ['handle', 'schemaVersion', 'title', 'scenes', 'comments']);
    if (episode.schemaVersion !== 2) throw new Error('Unsupported Workflow episode schema version.');
    const handle = text(episode.handle);
    const title = text(episode.title);
    if (!Array.isArray(episode.scenes) || !episode.scenes.length) throw new Error('Workflow episode requires scenes.');
    const ids = new Set<string>();
    const scenes: WorkflowScene[] = episode.scenes.map((value) => {
        let scene = record(value);
        if (scene.visualSource !== undefined) {
            if (scene.sceneKind !== 'trace') throw new Error('Scene visualSource requires a trace scene; concepts select sources per beat.');
            scene = resolveEpisodeVisualSource(scene, sources, 'trace', ['trace', 'operationPresentationMode']);
        }
        const id = text(scene.id);
        if (ids.has(id)) throw new Error(`Duplicate scene '${id}'.`);
        ids.add(id);
        const sceneTitle = text(scene.title);
        if (scene.sceneKind === 'trace') {
            fields(scene, ['sceneKind', 'id', 'title', 'trace', 'operationPresentationMode', 'beats', 'excerpt', 'direction', 'comments']);
            if (!Array.isArray(scene.beats)) throw new Error('Workflow trace scene requires beats.');
            const groupings = scene.beats.map(value => {
                const { grouping } = record(value);
                if (grouping !== undefined && grouping !== 'none' && grouping !== 'cases' && grouping !== 'macroSteps') {
                    throw new Error('Invalid Workflow beat grouping.');
                }
                return grouping;
            });
            const plan = parseTraceScenePlan({ schemaVersion: 1, title: sceneTitle, trace: scene.trace,
                beats: scene.beats.map(value => {
                    const beat = { ...record(value) };
                    delete beat.grouping;
                    return beat;
                }) }, effects => effects.map(effect => {
                    const parsed = parsePresentationEffect(effect, parseTraceTarget);
                    if (parsed.effectKind !== 'emphasis') throw new Error('Workflow trace teaching supports only emphasis.');
                    validateTeachingMarks(parsed.marks, parsed.target.targetKind);
                    return parsed;
                }));
            for (const beat of plan.beats) for (const segment of beat.presentation ?? []) {
                if (segment.cameraView !== undefined && segment.cameraView !== 'detail' && segment.cameraView !== 'overview') {
                    throw new Error('Unsupported Workflow trace teaching camera.');
                }
            }
            const trace = parseWorkflowTrace(plan.trace);
            const operationPresentationMode = parseOperationPresentationMode(scene.operationPresentationMode);
            const excerpt = parseEpisodeExcerpt(scene.excerpt);
            return { sceneKind: 'trace', id, title: sceneTitle, trace,
                ...parseEpisodeComments(scene.comments),
                ...(scene.direction === undefined ? {} : { direction: parseWorkflowSceneDirection(scene.direction) }),
                operationPresentationMode, beats: plan.beats.map((beat, index) => ({
                    ...beat, ...(groupings[index] === undefined ? {} : { grouping: groupings[index] }),
                })),
                ...(excerpt === undefined ? {} : { excerpt }) };
        }
        if (scene.sceneKind !== 'concept') throw new Error('Unknown Workflow scene kind.');
        fields(scene, ['sceneKind', 'id', 'title', 'beats', 'comments']);
        if (!Array.isArray(scene.beats) || !scene.beats.length) throw new Error('Concept scene requires beats.');
        const beatIds = new Set<string>();
        const beats: WorkflowConceptBeat[] = scene.beats.map((value) => {
            const beat = resolveEpisodeVisualSource(normalizeAuthoredBeat(value, 'concept'), sources, 'concept', ['arrangement']);
            fields(beat, ['id', 'arrangement', 'explanation', 'spokenText', 'narrationTextMap', 'narrationPauses', 'readingHoldMs', 'cues', 'presentation', 'comments']);
            const conceptArrangement = parseConceptArrangement(beat.arrangement);
            const id = text(beat.id);
            if (beatIds.has(id)) throw new Error(`Duplicate concept beat '${id}'.`);
            beatIds.add(id);
            if (typeof beat.readingHoldMs !== 'number' || !Number.isFinite(beat.readingHoldMs) || beat.readingHoldMs < 0) {
                throw new Error('Invalid concept reading hold.');
            }
            const explanation = text(beat.explanation);
            const spokenText = beat.spokenText === undefined ? undefined : text(beat.spokenText);
            const cues = beat.cues === undefined ? undefined : parseConceptCues(beat.cues, spokenText ?? explanation);
            const presentation = beat.presentation === undefined ? undefined
                : parseConceptPresentation(Array.isArray(beat.presentation)
                    ? beat.presentation.map(segment => resolveEpisodeVisualSource(record(segment), sources, 'concept', ['arrangement']))
                    : beat.presentation, cues, parseConceptArrangement, parseConceptTarget, { cameraView: true });
            for (const segment of presentation ?? []) {
                if (segment.textPanel !== undefined) throw new Error('Workflow concept text panels are unsupported.');
                for (const effect of segment.effects) {
                    if (effect.effectKind === 'emphasis') validateTeachingMarks(effect.marks, effect.target.targetKind);
                    if (effect.effectKind === 'emphasis' && effect.target.targetKind === 'natural-resource-tour'
                        && (conceptArrangement.content !== 'resource-types-and-resources'
                            || segment.cameraView !== 'natural-run' || effect.marks?.join(',') !== 'contour')) {
                        throw new Error('Natural Resource tour requires its radial camera and border-only emphasis.');
                    }
                }
                if (segment.cameraView !== undefined
                    && !['overview', 'detail', 'outside-start', 'outside-end', 'natural-start', 'natural-one', 'natural-two', 'natural-run', 'boolean-turn'].includes(segment.cameraView)) {
                    throw new Error('Unsupported Workflow concept cameraView.');
                }
                if (['natural-start', 'natural-one', 'natural-two', 'natural-run', 'boolean-turn'].includes(segment.cameraView ?? '')
                    && conceptArrangement.content !== 'resource-types-and-resources') {
                    throw new Error('Workflow radial camera requires Resource panels.');
                }
                if (segment.cameraView?.startsWith('outside-') && conceptArrangement.content !== 'resource-types-only') {
                    throw new Error('Workflow outside camera requires the ResourceType-only ring.');
                }
                if (segment.cameraView === 'detail' && !segment.effects.length) {
                    throw new Error('Workflow detail camera requires emphasis targets.');
                }
            }
            return { id, explanation, readingHoldMs: beat.readingHoldMs,
                ...parseEpisodeComments(beat.comments),
                ...(beat.narrationTextMap === undefined ? {} : {
                    narrationTextMap: parseNarrationTextMap(beat.narrationTextMap, text(beat.spokenText), explanation) }),
                ...(beat.narrationPauses === undefined ? {} : {
                    narrationPauses: parseNarrationPauses(beat.narrationPauses, spokenText ?? explanation) }),
                ...(spokenText === undefined ? {} : { spokenText }),
                ...(cues === undefined ? {} : { cues }),
                ...(presentation === undefined ? {} : { presentation }),
                arrangement: conceptArrangement };
        });
        return { sceneKind: 'concept', id, title: sceneTitle, beats, ...parseEpisodeComments(scene.comments) };
    });
    return { handle, title, schemaVersion: 2, scenes, ...parseEpisodeComments(episode.comments) };
}

export function detachWorkflowEpisode(state: RuntimeState): RuntimeState {
    const exploration = state.conceptArrangement ? state.explorationBeforeConcept : state;
    if (!exploration) throw new Error('Workflow concept scene lost its Exploration checkpoint.');
    return { ...exploration, episode: undefined, sceneIndex: undefined,
        conceptArrangement: undefined, conceptSum: undefined, conceptTool: undefined, conceptInvocation: undefined,
        explorationBeforeConcept: undefined, sceneOperationPresentationMode: undefined, sceneDirectionCatalogs: undefined,
        pendingReplayMove: null, isPlaying: false, accumulatedMs: 0, playbackAdvanceMode: 'manual' };
}
